import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Copy, Link2, Plus, RefreshCw, Search, Upload } from 'lucide-react';
import {
  CLUB_ROLE_DESCRIPTIONS,
  CLUB_ROLE_LABELS,
  TEAM_TIME_ZONES,
  formatMobile,
  type ClubDetail,
  type ClubPerson,
  type ClubPlayer,
  type ClubRole,
  type ClubSeason,
  type ClubTeam,
  type ClubTeamDetail,
  type PlayhqSeason,
  type PlayhqTeam,
} from '@hoopify/shared';
import { api, errorMessage, fieldErrors } from '../lib/api';
import { useMe } from '../lib/session';
import { formatShortDay } from '../lib/dates';
import { timeAgo } from '../lib/format';
import { Alert, Button, Dialog, Empty, Field, LoadingBlock, Menu, MenuItem, Section, TextArea } from '../components/ui';
import { ClubPlayerDialog, TeamStatusBadge, useClub } from '../components/clubs/ClubParts';
import { StateSelect } from './admin/AdminClubsPage';
import { ClubStatusBadge } from './ClubsPage';

type Tab = 'teams' | 'players' | 'seasons' | 'people' | 'playhq' | 'settings';

export function ClubPage() {
  const { clubId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const club = useClub(clubId);
  if (club.isLoading) return <LoadingBlock />;
  if (club.isError || !club.data) {
    return (
      <div className="space-y-4">
        <Alert>{errorMessage(club.error)}</Alert>
        <Link to="/teams" className="btn-secondary">
          Back to teams
        </Link>
      </div>
    );
  }
  const c = club.data;
  const admin = c.permissions.includes('club:manage');
  const tabs: { id: Tab; label: string }[] = [
    { id: 'teams', label: 'Teams' },
    { id: 'players', label: 'Players' },
    { id: 'seasons', label: 'Seasons' },
    { id: 'people', label: 'People' },
    ...(admin ? [{ id: 'playhq' as Tab, label: 'PlayHQ' }, { id: 'settings' as Tab, label: 'Settings' }] : []),
  ];
  const requested = params.get('tab') as Tab | null;
  const tab: Tab = tabs.some((x) => x.id === requested) ? requested! : 'teams';

  return (
    <>
      <header className="mb-6">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h1 className="text-[24px] leading-tight sm:text-[28px]">{c.name}</h1>
          <span className="inline-flex rounded bg-sunken px-1.5 py-0.5 text-[12px] font-medium text-muted">{CLUB_ROLE_LABELS[c.role]}</span>
          {c.status !== 'approved' && <ClubStatusBadge status={c.status} />}
        </div>
        <p className="mt-1 text-muted">
          {[[c.suburb, c.state].filter(Boolean).join(' '), c.status === 'approved' ? `${c.counts.teams} ${c.counts.teams === 1 ? 'team' : 'teams'}, ${c.counts.players} ${c.counts.players === 1 ? 'player' : 'players'}` : null].filter(Boolean).join(' · ')}
        </p>
      </header>

      {c.status !== 'approved' ? (
        <NotApproved club={c} />
      ) : (
        <>
          <nav className="mb-6 flex gap-1 overflow-x-auto border-b border-line" aria-label="Club sections">
            {tabs.map((x) => (
              <button
                key={x.id}
                onClick={() => setParams(x.id === 'teams' ? {} : { tab: x.id }, { replace: true })}
                className={`-mb-px shrink-0 border-b-2 px-3 py-2 text-[14px] font-medium whitespace-nowrap ${tab === x.id ? 'border-action text-ink' : 'border-transparent text-muted hover:text-ink'}`}
                aria-current={tab === x.id ? 'page' : undefined}
              >
                {x.label}
              </button>
            ))}
          </nav>
          {tab === 'teams' && <TeamsTab club={c} />}
          {tab === 'players' && <PlayersTab club={c} />}
          {tab === 'seasons' && <SeasonsTab club={c} />}
          {tab === 'people' && <PeopleTab club={c} />}
          {tab === 'playhq' && <PlayhqTab club={c} />}
          {tab === 'settings' && <SettingsTab club={c} />}
        </>
      )}
    </>
  );
}

function NotApproved({ club: c }: { club: ClubDetail }) {
  if (c.status === 'pending') {
    return (
      <div className="max-w-[620px] rounded-lg border border-line bg-surface p-5">
        <p className="font-semibold">Hoopify is checking your club</p>
        <p className="mt-1 text-[14px] text-muted">
          We’ll call {c.contactName} on {formatMobile(c.mobile)} to make sure it’s really your club, usually within a day or two. You’ll get an email at {c.contactEmail} once it’s approved, and then you can set up seasons, teams and players.
        </p>
      </div>
    );
  }
  return (
    <Alert tone={c.status === 'suspended' ? 'warn' : 'danger'}>
      {c.status === 'suspended' ? 'Hoopify has paused this club for now, so its pages can’t be used. Its teams carry on as normal.' : 'Hoopify didn’t approve this club.'}
      {c.decisionNote ? ` Reason: ${c.decisionNote}` : ''} If you think this is a mistake, reply to our email.
    </Alert>
  );
}

// ---------- Teams ----------

function useSeasons(clubId: string) {
  return useQuery({ queryKey: ['club-seasons', clubId], queryFn: () => api.get<ClubSeason[]>(`/clubs/${clubId}/seasons`) });
}

function TeamsTab({ club }: { club: ClubDetail }) {
  const admin = club.permissions.includes('club:manage');
  const seasons = useSeasons(club.id);
  const [season, setSeason] = useState<string>('');
  const teams = useQuery({ queryKey: ['club-teams', club.id, season], queryFn: () => api.get<ClubTeam[]>(`/clubs/${club.id}/teams${season ? `?season=${season}` : ''}`) });
  const [dialog, setDialog] = useState<'new' | 'invite' | null>(null);
  const drafts = teams.data?.filter((t) => t.status !== 'live').length ?? 0;

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        {!!seasons.data?.length && (
          <select className="field-input w-auto py-1.5 text-[14px]" value={season} onChange={(e) => setSeason(e.target.value)} aria-label="Season">
            <option value="">All seasons</option>
            {seasons.data.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        )}
        <span className="flex-1" />
        {admin && (
          <>
            <Button variant="secondary" onClick={() => setDialog('invite')}>
              <Link2 className="size-4" aria-hidden /> Add a team already on Hoopify
            </Button>
            <Button onClick={() => setDialog('new')}>
              <Plus className="size-4" aria-hidden /> New team
            </Button>
          </>
        )}
      </div>
      {drafts > 0 && <p className="mb-3 text-[14px] text-muted">{drafts === 1 ? '1 team is' : `${drafts} teams are`} still a draft: nobody on {drafts === 1 ? 'it has' : 'them has'} been emailed.</p>}
      {teams.isLoading ? (
        <LoadingBlock />
      ) : teams.isError ? (
        <Alert>{errorMessage(teams.error)}</Alert>
      ) : !teams.data?.length ? (
        <Empty title="No teams yet" action={admin ? <Button onClick={() => setDialog('new')}>New team</Button> : undefined}>
          {admin
            ? 'Set up a team, add its coaches and players, then choose when the invites go out. If you use PlayHQ, you can import your teams from the PlayHQ tab.'
            : 'A club admin sets up the teams. Then you can add coaches and players.'}
        </Empty>
      ) : (
        <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-surface">
          {teams.data.map((t) => (
            <li key={t.id}>
              <Link to={`/clubs/${club.id}/teams/${t.id}`} className="flex items-center gap-4 px-4 py-3.5 hover:bg-sunken sm:px-5">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                    <span className="font-medium">{t.name}</span>
                    <TeamStatusBadge team={t} />
                  </div>
                  <p className="mt-0.5 text-[13px] text-muted">
                    {[t.ageGroup, !season && t.seasonName, `${t.coaches} ${t.coaches === 1 ? 'coach' : 'coaches'}`, `${t.players} ${t.players === 1 ? 'player' : 'players'}`, t.playhq ? `PlayHQ: ${t.playhq.gradeName ?? 'linked'}` : null].filter(Boolean).join(' · ')}
                  </p>
                  {t.playhq?.error && <p className="mt-0.5 text-[13px] text-warn">PlayHQ: {t.playhq.error}</p>}
                </div>
                <ChevronRight className="size-4 shrink-0 text-faint" aria-hidden />
              </Link>
            </li>
          ))}
        </ul>
      )}
      {admin && <TeamInvites club={club} />}
      {dialog === 'new' && <NewTeamDialog club={club} seasons={seasons.data ?? []} defaultSeason={season} onClose={() => setDialog(null)} />}
      {dialog === 'invite' && <InviteTeamDialog club={club} seasons={seasons.data ?? []} onClose={() => setDialog(null)} />}
    </>
  );
}

function SeasonSelect({ seasons, value, onChange, error }: { seasons: ClubSeason[]; value: string; onChange: (v: string) => void; error?: string }) {
  return (
    <div className="space-y-1.5">
      <label htmlFor="club-season" className="label-caps block">
        Season
      </label>
      <select id="club-season" className="field-input" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">No season</option>
        {seasons.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
          </option>
        ))}
      </select>
      {error && <p className="text-[13px] text-danger">{error}</p>}
    </div>
  );
}

function NewTeamDialog({ club, seasons, defaultSeason, onClose }: { club: ClubDetail; seasons: ClubSeason[]; defaultSeason: string; onClose: () => void }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [v, setV] = useState({ name: '', ageGroup: '', clubSeasonId: defaultSeason || seasons[0]?.id || '' });
  const create = useMutation({
    mutationFn: () => api.post<ClubTeamDetail>(`/clubs/${club.id}/teams`, { ...v, clubSeasonId: v.clubSeasonId || null }),
    onSuccess: async (t) => {
      await qc.invalidateQueries({ queryKey: ['club-teams', club.id] });
      navigate(`/clubs/${club.id}/teams/${t.id}`);
    },
  });
  const f = fieldErrors(create.error);
  return (
    <Dialog open onClose={onClose} title="New team">
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); create.mutate(); }} className="space-y-4" noValidate>
        <p className="text-[14px] text-muted">The team starts as a draft. Nobody is emailed until you send the invites.</p>
        {create.isError && !Object.keys(f).length && <Alert>{errorMessage(create.error)}</Alert>}
        <Field label="Team name" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} error={f.name} placeholder="e.g. U12 Girls Blue" autoFocus />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Age group (optional)" value={v.ageGroup} onChange={(e) => setV({ ...v, ageGroup: e.target.value })} error={f.ageGroup} placeholder="e.g. U12" />
          <SeasonSelect seasons={seasons} value={v.clubSeasonId} onChange={(s) => setV({ ...v, clubSeasonId: s })} error={f.clubSeasonId} />
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={create.isPending}>
            Create draft team
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

interface TeamInviteRow {
  id: string;
  email: string;
  status: 'pending' | 'declined';
  seasonName: string | null;
  createdAt: string;
}

function TeamInvites({ club }: { club: ClubDetail }) {
  const qc = useQueryClient();
  const key = ['club-team-invites', club.id];
  const list = useQuery({ queryKey: key, queryFn: () => api.get<TeamInviteRow[]>(`/clubs/${club.id}/team-invites`) });
  const cancel = useMutation({ mutationFn: (id: string) => api.del<TeamInviteRow[]>(`/clubs/${club.id}/team-invites/${id}`), onSuccess: (r) => qc.setQueryData(key, r) });
  if (!list.data?.length) return null;
  return (
    <div className="mt-6">
      <h2 className="mb-2 text-[15px] font-semibold">Teams asked to join</h2>
      <ul className="divide-y divide-line rounded-lg border border-line bg-surface text-[14px]">
        {list.data.map((i) => (
          <li key={i.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5">
            <span className="min-w-0 flex-1">
              <span className="font-medium">{i.email}</span>
              <span className="text-muted">
                {' '}
                · {i.status === 'declined' ? 'said no' : `asked ${timeAgo(i.createdAt)}`}
                {i.seasonName ? ` · ${i.seasonName}` : ''}
              </span>
            </span>
            <button className="text-[13px] font-medium text-action hover:underline" onClick={() => cancel.mutate(i.id)} disabled={cancel.isPending}>
              {i.status === 'declined' ? 'Clear' : 'Cancel'}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function InviteTeamDialog({ club, seasons, onClose }: { club: ClubDetail; seasons: ClubSeason[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [season, setSeason] = useState(seasons[0]?.id ?? '');
  const send = useMutation({
    mutationFn: () => api.post<TeamInviteRow[]>(`/clubs/${club.id}/team-invites`, { email, clubSeasonId: season || null }),
    onSuccess: (r) => {
      qc.setQueryData(['club-team-invites', club.id], r);
      onClose();
    },
  });
  const f = fieldErrors(send.error);
  return (
    <Dialog open onClose={onClose} title="Add a team already on Hoopify">
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); send.mutate(); }} className="space-y-4" noValidate>
        <p className="text-[14px] text-muted">
          We’ll email the head coach. They choose which of their teams joins the club. The club can then help with its coaches, players and PlayHQ fixture, but can’t see sessions or plans.
        </p>
        {send.isError && !Object.keys(f).length && <Alert>{errorMessage(send.error)}</Alert>}
        <Field label="Head coach’s email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} error={f.email} autoFocus />
        <SeasonSelect seasons={seasons} value={season} onChange={setSeason} error={f.clubSeasonId} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={send.isPending}>
            Send email
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ---------- Players ----------

function PlayersTab({ club }: { club: ClubDetail }) {
  const people = club.permissions.includes('club:people');
  const players = useQuery({ queryKey: ['club-players', club.id], queryFn: () => api.get<ClubPlayer[]>(`/clubs/${club.id}/players`) });
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState<ClubPlayer | 'new' | null>(null);
  const [pasting, setPasting] = useState(false);
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (players.data ?? []).filter((p) => !s || p.name.toLowerCase().includes(s) || p.contacts.some((c) => c.email.includes(s)) || p.teams.some((t) => t.teamName.toLowerCase().includes(s)));
  }, [players.data, q]);

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[200px] flex-1">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-faint" aria-hidden />
          <input className="field-input pl-9" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email or team" aria-label="Search players" />
        </div>
        {people && (
          <>
            <Button variant="secondary" onClick={() => setPasting(true)}>
              <Upload className="size-4" aria-hidden /> Paste a list
            </Button>
            <Button onClick={() => setEditing('new')}>
              <Plus className="size-4" aria-hidden /> Add player
            </Button>
          </>
        )}
      </div>
      {players.isLoading ? (
        <LoadingBlock />
      ) : players.isError ? (
        <Alert>{errorMessage(players.error)}</Alert>
      ) : !players.data?.length ? (
        <Empty title="No players yet">Add your club’s players once, with their parents’ emails, then put them in teams each season.</Empty>
      ) : (
        <>
          <p className="tnum mb-2 text-[14px] text-muted">
            {shown.length === players.data.length ? `${players.data.length} players` : `${shown.length} of ${players.data.length} players`}
          </p>
          <ul className="divide-y divide-line rounded-lg border border-line bg-surface">
            {shown.map((p) => (
              <li key={p.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{p.name}</p>
                  <p className="truncate text-[13px] text-muted">{p.contacts.length ? p.contacts.map((c) => `${c.email}${c.kind === 'parent' ? ' (parent)' : ''}`).join(', ') : 'No email yet'}</p>
                </div>
                <p className="text-[13px] text-muted sm:w-[220px] sm:text-right">{p.teams.length ? p.teams.map((t) => t.teamName).join(', ') : <span className="text-warn">Not in a team</span>}</p>
                {people && (
                  <button className="btn-ghost px-2 py-1 text-[13px]" onClick={() => setEditing(p)}>
                    Edit
                  </button>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
      {editing && <ClubPlayerDialog clubId={club.id} player={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
      {pasting && <PasteDialog clubId={club.id} onClose={() => setPasting(false)} />}
    </>
  );
}

function PasteDialog({ clubId, onClose }: { clubId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const save = useMutation({
    mutationFn: () => api.post<ClubPlayer[]>(`/clubs/${clubId}/players/bulk`, { text }),
    onSuccess: (r) => {
      qc.setQueryData(['club-players', clubId], r);
      void qc.invalidateQueries({ queryKey: ['club', clubId] });
      onClose();
    },
  });
  const lines = text.split('\n').filter((l) => l.trim()).length;
  return (
    <Dialog open onClose={onClose} title="Paste a list of players">
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); save.mutate(); }} className="space-y-4" noValidate>
        <TextArea label="One player per line" value={text} onChange={(e) => setText(e.target.value)} rows={8} placeholder={'Mia Chen\nAva Smith ava.smith@example.com\nLeo Park'} hint="An email on the line is taken as the player’s own. Add parents’ emails by editing each player." autoFocus />
        {save.isError && <Alert>{errorMessage(save.error)}</Alert>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={save.isPending} disabled={!lines}>
            Add {lines || ''} {lines === 1 ? 'player' : 'players'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ---------- Seasons ----------

function SeasonsTab({ club }: { club: ClubDetail }) {
  const admin = club.permissions.includes('club:manage');
  const seasons = useSeasons(club.id);
  const [editing, setEditing] = useState<ClubSeason | 'new' | null>(null);
  const [copying, setCopying] = useState<ClubSeason | null>(null);
  return (
    <>
      <div className="mb-4 flex items-center justify-between gap-3">
        <p className="max-w-[60ch] text-[14px] text-muted">Group teams by season. Set up next season early from this one: teams, coaches and players come across as drafts.</p>
        {admin && (
          <Button onClick={() => setEditing('new')}>
            <Plus className="size-4" aria-hidden /> New season
          </Button>
        )}
      </div>
      {seasons.isLoading ? (
        <LoadingBlock />
      ) : !seasons.data?.length ? (
        <Empty title="No seasons yet">{admin ? 'Add a season, like “Winter 2027”, to group your teams.' : 'A club admin adds the seasons.'}</Empty>
      ) : (
        <ul className="divide-y divide-line rounded-lg border border-line bg-surface">
          {seasons.data.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="font-medium">{s.name}</p>
                <p className="text-[13px] text-muted">
                  {[s.startDate && s.endDate ? `${formatShortDay(s.startDate)} to ${formatShortDay(s.endDate)}` : null, `${s.teamCount} ${s.teamCount === 1 ? 'team' : 'teams'}`].filter(Boolean).join(' · ')}
                </p>
              </div>
              {admin && (
                <Menu label="Options">
                  {(close) => (
                    <>
                      <MenuItem onClick={() => { close(); setCopying(s); }}>
                        <Copy className="size-4" aria-hidden /> Copy teams from another season…
                      </MenuItem>
                      <MenuItem onClick={() => { close(); setEditing(s); }}>Edit</MenuItem>
                    </>
                  )}
                </Menu>
              )}
            </li>
          ))}
        </ul>
      )}
      {editing && <SeasonDialog club={club} season={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
      {copying && <CopySeasonDialog club={club} to={copying} seasons={seasons.data ?? []} onClose={() => setCopying(null)} />}
    </>
  );
}

function SeasonDialog({ club, season, onClose }: { club: ClubDetail; season: ClubSeason | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [v, setV] = useState({ name: season?.name ?? '', startDate: season?.startDate ?? '', endDate: season?.endDate ?? '' });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['club-seasons', club.id] });
    void qc.invalidateQueries({ queryKey: ['club-teams', club.id] });
    void qc.invalidateQueries({ queryKey: ['club', club.id] });
  };
  const save = useMutation({
    mutationFn: () => {
      const body = { name: v.name, startDate: v.startDate || null, endDate: v.endDate || null };
      return season ? api.put(`/clubs/${club.id}/seasons/${season.id}`, body) : api.post(`/clubs/${club.id}/seasons`, body);
    },
    onSuccess: () => {
      refresh();
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () => api.del(`/clubs/${club.id}/seasons/${season!.id}`),
    onSuccess: () => {
      refresh();
      onClose();
    },
  });
  const f = fieldErrors(save.error);
  return (
    <Dialog open onClose={onClose} title={season ? `Edit ${season.name}` : 'New season'}>
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); save.mutate(); }} className="space-y-4" noValidate>
        {(save.isError || remove.isError) && !Object.keys(f).length && <Alert>{errorMessage(save.error ?? remove.error)}</Alert>}
        <Field label="Name" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} error={f.name} placeholder="e.g. Winter 2027" autoFocus />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Starts (optional)" type="date" value={v.startDate} onChange={(e) => setV({ ...v, startDate: e.target.value })} error={f.startDate} />
          <Field label="Ends (optional)" type="date" value={v.endDate} min={v.startDate || undefined} onChange={(e) => setV({ ...v, endDate: e.target.value })} error={f.endDate} />
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          {season && (
            <Button type="button" variant="danger" className="btn-danger mr-auto" loading={remove.isPending} onClick={() => remove.mutate()} title="Its teams are kept">
              Remove season
            </Button>
          )}
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={save.isPending}>
            {season ? 'Save' : 'Add season'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function CopySeasonDialog({ club, to, seasons, onClose }: { club: ClubDetail; to: ClubSeason; seasons: ClubSeason[]; onClose: () => void }) {
  const qc = useQueryClient();
  const others = seasons.filter((s) => s.id !== to.id && s.teamCount > 0);
  const [from, setFrom] = useState(others[0]?.id ?? '');
  const [ageStep, setAgeStep] = useState<0 | 1 | 2>(1);
  const copy = useMutation({
    mutationFn: () => api.post<ClubTeam[]>(`/clubs/${club.id}/seasons/${to.id}/copy`, { fromSeasonId: from, ageStep }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['club-seasons', club.id] });
      void qc.invalidateQueries({ queryKey: ['club-teams', club.id] });
      onClose();
    },
  });
  const n = others.find((s) => s.id === from)?.teamCount ?? 0;
  return (
    <Dialog open onClose={onClose} title={`Set up ${to.name}`}>
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); copy.mutate(); }} className="space-y-4" noValidate>
        {!others.length ? (
          <Alert tone="info">There’s no other season with teams to copy from yet.</Alert>
        ) : (
          <>
            <p className="text-[14px] text-muted">Each team comes across as a draft, with the same coaches and players. Nobody is emailed until you send each team’s invites.</p>
            <div className="space-y-1.5">
              <label htmlFor="copy-from" className="label-caps block">
                Copy from
              </label>
              <select id="copy-from" className="field-input" value={from} onChange={(e) => setFrom(e.target.value)}>
                {others.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.teamCount} {s.teamCount === 1 ? 'team' : 'teams'})
                  </option>
                ))}
              </select>
            </div>
            <fieldset>
              <legend className="mb-1.5 text-[14px] font-medium">Age groups</legend>
              <div className="grid grid-cols-3 gap-1 rounded-md bg-sunken p-1 text-[14px]">
                {([0, 1, 2] as const).map((x) => (
                  <button type="button" key={x} onClick={() => setAgeStep(x)} aria-pressed={ageStep === x} className={`rounded py-1 font-medium ${ageStep === x ? 'bg-surface text-ink shadow-sm' : 'text-muted'}`}>
                    {x === 0 ? 'Keep' : `Up ${x}`}
                  </button>
                ))}
              </div>
              <p className="mt-1 text-[13px] text-muted">{ageStep === 0 ? 'U12 stays U12.' : `U12 becomes U${12 + ageStep}, in team names too.`}</p>
            </fieldset>
          </>
        )}
        {copy.isError && <Alert>{errorMessage(copy.error)}</Alert>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={copy.isPending} disabled={!from}>
            Copy {n} {n === 1 ? 'team' : 'teams'} as drafts
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ---------- People who run the club ----------

function PeopleTab({ club }: { club: ClubDetail }) {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const admin = club.permissions.includes('club:manage');
  const key = ['club-people', club.id];
  const people = useQuery({ queryKey: key, queryFn: () => api.get<ClubPerson[]>(`/clubs/${club.id}/people`) });
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<ClubRole>('registrar');
  const invite = useMutation({
    mutationFn: () => api.post<ClubPerson[]>(`/clubs/${club.id}/people`, { email, role }),
    onSuccess: (r) => {
      qc.setQueryData(key, r);
      setEmail('');
    },
  });
  const act = useMutation({
    mutationFn: (a: { kind: 'cancel' | 'remove' | 'role'; p: ClubPerson; role?: ClubRole }) =>
      a.kind === 'cancel'
        ? api.del<ClubPerson[]>(`/clubs/${club.id}/people/invites/${a.p.inviteId}`)
        : a.kind === 'role'
          ? api.patch<ClubPerson[]>(`/clubs/${club.id}/people/${a.p.userId}`, { role: a.role })
          : api.del(`/clubs/${club.id}/people/${a.p.userId}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key }),
  });
  const leave = useMutation({
    mutationFn: () => api.post(`/clubs/${club.id}/leave`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['clubs'] });
      navigate('/teams');
    },
  });
  const f = fieldErrors(invite.error);
  return (
    <>
      <Section title="Who runs the club" description="Club admins run everything. Registrars look after players and coaches, and send the invites.">
        {people.isLoading ? (
          <LoadingBlock />
        ) : (
          <ul className="divide-y divide-line rounded-lg border border-line bg-surface">
            {people.data?.map((p) => (
              <li key={p.userId ?? p.inviteId} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="font-medium">
                    {p.name ?? p.email}
                    {p.userId === me.id && <span className="font-normal text-muted"> (you)</span>}
                  </p>
                  <p className="text-[13px] text-muted">{p.status === 'invited' ? `Invited: ${p.email}` : p.email}</p>
                </div>
                {admin && p.status === 'member' && p.userId !== me.id ? (
                  <select className="field-input w-auto py-1 text-[14px]" value={p.role} onChange={(e) => act.mutate({ kind: 'role', p, role: e.target.value as ClubRole })} aria-label={`${p.name}’s role`}>
                    <option value="admin">{CLUB_ROLE_LABELS.admin}</option>
                    <option value="registrar">{CLUB_ROLE_LABELS.registrar}</option>
                  </select>
                ) : (
                  <span className="text-[13px] text-muted">{CLUB_ROLE_LABELS[p.role]}</span>
                )}
                {admin && p.userId !== me.id && (
                  <button className="text-[13px] font-medium text-danger hover:underline" onClick={() => act.mutate({ kind: p.status === 'invited' ? 'cancel' : 'remove', p })}>
                    {p.status === 'invited' ? 'Cancel invite' : 'Remove'}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {act.isError && (
          <div className="mt-3">
            <Alert>{errorMessage(act.error)}</Alert>
          </div>
        )}
      </Section>
      {admin && (
        <Section title="Invite someone to help">
          <form onSubmit={(e: FormEvent) => { e.preventDefault(); invite.mutate(); }} className="max-w-[560px] space-y-3" noValidate>
            <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
              <Field label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} error={f.email} />
              <div className="space-y-1.5">
                <label htmlFor="club-role" className="label-caps block">
                  Role
                </label>
                <select id="club-role" className="field-input" value={role} onChange={(e) => setRole(e.target.value as ClubRole)}>
                  <option value="registrar">{CLUB_ROLE_LABELS.registrar}</option>
                  <option value="admin">{CLUB_ROLE_LABELS.admin}</option>
                </select>
              </div>
            </div>
            <p className="text-[13px] text-muted">{CLUB_ROLE_DESCRIPTIONS[role]}</p>
            {invite.isError && !f.email && <Alert>{errorMessage(invite.error)}</Alert>}
            <Button type="submit" loading={invite.isPending} disabled={!email.trim()}>
              Send invite
            </Button>
          </form>
        </Section>
      )}
      <Section title="Stop helping run the club">
        <p className="mb-3 text-[14px] text-muted">You’ll stay on any teams you coach. A club needs at least one club admin.</p>
        {leave.isError && (
          <div className="mb-3">
            <Alert>{errorMessage(leave.error)}</Alert>
          </div>
        )}
        <Button variant="secondary" loading={leave.isPending} onClick={() => leave.mutate()}>
          Leave {club.name}
        </Button>
      </Section>
    </>
  );
}

// ---------- PlayHQ ----------

function PlayhqTab({ club }: { club: ClubDetail }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(!club.playhq.connected);
  const [v, setV] = useState({ tenant: club.playhq.tenant ?? 'bv', orgId: club.playhq.orgId ?? '', apiKey: '' });
  const save = useMutation({
    mutationFn: () => api.put<ClubDetail>(`/clubs/${club.id}/playhq`, v),
    onSuccess: (c) => {
      qc.setQueryData(['club', club.id], c);
      setEditing(false);
      setV({ ...v, apiKey: '' });
    },
  });
  const disconnect = useMutation({ mutationFn: () => api.del<ClubDetail>(`/clubs/${club.id}/playhq`), onSuccess: (c) => qc.setQueryData(['club', club.id], c) });
  const sync = useMutation({
    mutationFn: () => api.post<{ teams: number; failed: number; added: number; updated: number; removed: number }>(`/clubs/${club.id}/playhq/sync`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['club', club.id] });
      void qc.invalidateQueries({ queryKey: ['club-teams', club.id] });
    },
  });
  const f = fieldErrors(save.error);
  return (
    <>
      <Section
        title="Connection"
        description="PlayHQ gives your teams’ fixtures, results and ladders. Ask your association for the club’s API key and organisation ID. Hoopify only reads from PlayHQ: nothing is changed there."
      >
        {club.playhq.connected && !editing ? (
          <div className="space-y-3">
            <p className="text-[14px]">
              <span className="font-medium text-ok">Connected</span> <span className="text-muted">({club.playhq.tenant}, organisation {club.playhq.orgId?.slice(0, 8)}…)</span>
            </p>
            <p className="text-[14px] text-muted">
              Linked teams update every 3 hours{club.playhq.syncedAt ? `; last ${timeAgo(club.playhq.syncedAt)}` : ''}.
            </p>
            {sync.data && (
              <Alert tone={sync.data.failed ? 'warn' : 'ok'}>
                {sync.data.teams === 0
                  ? 'No teams are linked to PlayHQ yet.'
                  : `Synced ${sync.data.teams} ${sync.data.teams === 1 ? 'team' : 'teams'}: ${sync.data.added} new games, ${sync.data.updated} updated, ${sync.data.removed} removed.${sync.data.failed ? ` ${sync.data.failed} couldn’t be synced; see the Teams tab.` : ''}`}
              </Alert>
            )}
            {sync.isError && <Alert>{errorMessage(sync.error)}</Alert>}
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" loading={sync.isPending} onClick={() => sync.mutate()}>
                <RefreshCw className="size-4" aria-hidden /> Sync now
              </Button>
              <Button variant="secondary" onClick={() => setEditing(true)}>
                Change details
              </Button>
              <Button variant="ghost" loading={disconnect.isPending} onClick={() => disconnect.mutate()}>
                Disconnect
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={(e: FormEvent) => { e.preventDefault(); save.mutate(); }} className="max-w-[520px] space-y-4" noValidate>
            {save.isError && !Object.keys(f).length && <Alert>{errorMessage(save.error)}</Alert>}
            <div className="grid gap-4 sm:grid-cols-[120px_1fr]">
              <Field label="Sport" value={v.tenant} onChange={(e) => setV({ ...v, tenant: e.target.value })} error={f.tenant} hint="bv for Basketball Victoria" />
              <Field label="Organisation ID" value={v.orgId} onChange={(e) => setV({ ...v, orgId: e.target.value })} error={f.orgId} placeholder="1a2b3c4d-…" />
            </div>
            <Field
              label="API key"
              type="password"
              autoComplete="off"
              value={v.apiKey}
              onChange={(e) => setV({ ...v, apiKey: e.target.value })}
              error={f.apiKey}
              hint={club.playhq.connected ? 'Leave blank to keep the saved key.' : 'Stored encrypted. Only used to read your club’s PlayHQ data.'}
            />
            <div className="flex gap-2">
              <Button type="submit" loading={save.isPending}>
                Check and save
              </Button>
              {club.playhq.connected && (
                <Button type="button" variant="secondary" onClick={() => setEditing(false)}>
                  Cancel
                </Button>
              )}
            </div>
          </form>
        )}
      </Section>
      {club.playhq.connected && <ImportTeams club={club} />}
    </>
  );
}

function ImportTeams({ club }: { club: ClubDetail }) {
  const qc = useQueryClient();
  const seasons = useQuery({ queryKey: ['phq-seasons', club.id], queryFn: () => api.get<PlayhqSeason[]>(`/clubs/${club.id}/playhq/seasons`) });
  const ourSeasons = useSeasons(club.id);
  const [phqSeason, setPhqSeason] = useState('');
  const teams = useQuery({ queryKey: ['phq-teams', club.id, phqSeason], queryFn: () => api.get<PlayhqTeam[]>(`/clubs/${club.id}/playhq/seasons/${phqSeason}/teams`), enabled: !!phqSeason });
  const [picked, setPicked] = useState<string[]>([]);
  const [into, setInto] = useState('');
  const run = useMutation({
    mutationFn: () => api.post<{ created: number; skipped: number }>(`/clubs/${club.id}/playhq/import`, { clubSeasonId: into || null, teams: teams.data!.filter((t) => picked.includes(t.id)).map(({ id, name, gradeId, gradeName }) => ({ id, name, gradeId, gradeName })) }),
    onSuccess: () => {
      setPicked([]);
      void qc.invalidateQueries({ queryKey: ['phq-teams', club.id] });
      void qc.invalidateQueries({ queryKey: ['club-teams', club.id] });
      void qc.invalidateQueries({ queryKey: ['club', club.id] });
    },
  });
  const free = teams.data?.filter((t) => !t.linkedTeamId) ?? [];
  return (
    <Section title="Import teams" description="Bring your club’s PlayHQ teams in as draft teams, with their fixtures. Then add coaches and players to each.">
      {seasons.isLoading ? (
        <LoadingBlock />
      ) : seasons.isError ? (
        <Alert>{errorMessage(seasons.error)}</Alert>
      ) : (
        <div className="max-w-[640px] space-y-4">
          <div className="space-y-1.5">
            <label htmlFor="phq-season" className="label-caps block">
              PlayHQ season
            </label>
            <select id="phq-season" className="field-input" value={phqSeason} onChange={(e) => { setPhqSeason(e.target.value); setPicked([]); }}>
              <option value="">Choose a season</option>
              {seasons.data?.map((s) => (
                <option key={s.id} value={s.id}>
                  {[s.name, s.competition, s.association].filter(Boolean).join(' · ')}
                </option>
              ))}
            </select>
          </div>
          {teams.isLoading && <LoadingBlock />}
          {teams.isError && <Alert>{errorMessage(teams.error)}</Alert>}
          {teams.data && (
            <>
              {!teams.data.length ? (
                <p className="text-muted">PlayHQ has no teams for your club in that season.</p>
              ) : (
                <ul className="divide-y divide-line rounded-lg border border-line bg-surface">
                  {teams.data.map((t) => (
                    <li key={t.id}>
                      <label className={`flex items-center gap-3 px-4 py-2.5 ${t.linkedTeamId ? 'opacity-60' : 'cursor-pointer hover:bg-sunken'}`}>
                        <input
                          type="checkbox"
                          className="size-4 accent-[var(--action)]"
                          disabled={!!t.linkedTeamId}
                          checked={!!t.linkedTeamId || picked.includes(t.id)}
                          onChange={(e) => setPicked(e.target.checked ? [...picked, t.id] : picked.filter((x) => x !== t.id))}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="font-medium">{t.name}</span>
                          <span className="block text-[13px] text-muted">{t.gradeName ?? 'No grade'}</span>
                        </span>
                        {t.linkedTeamId && <span className="text-[13px] text-muted">Already in Hoopify</span>}
                      </label>
                    </li>
                  ))}
                </ul>
              )}
              {free.length > 0 && (
                <div className="flex flex-wrap items-end gap-3">
                  <div className="min-w-[200px] flex-1">
                    <SeasonSelect seasons={ourSeasons.data ?? []} value={into} onChange={setInto} />
                  </div>
                  <Button variant="secondary" onClick={() => setPicked(picked.length === free.length ? [] : free.map((t) => t.id))}>
                    {picked.length === free.length ? 'Clear' : 'Select all'}
                  </Button>
                  <Button loading={run.isPending} disabled={!picked.length} onClick={() => run.mutate()}>
                    Import {picked.length || ''} {picked.length === 1 ? 'team' : 'teams'}
                  </Button>
                </div>
              )}
              {run.data && <Alert tone="ok">Imported {run.data.created} {run.data.created === 1 ? 'team' : 'teams'} as drafts. Find them on the Teams tab.</Alert>}
              {run.isError && <Alert>{errorMessage(run.error)}</Alert>}
            </>
          )}
        </div>
      )}
    </Section>
  );
}

// ---------- Settings ----------

function SettingsTab({ club }: { club: ClubDetail }) {
  const qc = useQueryClient();
  const [v, setV] = useState({ name: club.name, mobile: club.mobile.replace(/^\+61/, '0'), suburb: club.suburb ?? '', state: club.state ?? '', website: club.website ?? '', timeZone: club.timeZone });
  const save = useMutation({
    mutationFn: () => api.patch<ClubDetail>(`/clubs/${club.id}`, { ...v, state: v.state || null }),
    onSuccess: (c) => {
      qc.setQueryData(['club', club.id], c);
      void qc.invalidateQueries({ queryKey: ['clubs'] });
    },
  });
  const f = fieldErrors(save.error);
  const set = (k: keyof typeof v) => (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value });
  return (
    <Section title="Club details">
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); save.mutate(); }} className="max-w-[520px] space-y-4" noValidate>
        <Field label="Club name" value={v.name} onChange={set('name')} error={f.name} />
        <Field label="Contact mobile" type="tel" value={v.mobile} onChange={set('mobile')} error={f.mobile} hint="Only Hoopify sees this." />
        <div className="grid grid-cols-[1fr_auto] gap-4">
          <Field label="Suburb" value={v.suburb} onChange={set('suburb')} error={f.suburb} />
          <StateSelect value={v.state} onChange={(s) => setV({ ...v, state: s })} />
        </div>
        <Field label="Website" value={v.website} onChange={set('website')} error={f.website} />
        <div className="space-y-1.5">
          <label htmlFor="club-tz" className="label-caps block">
            Time zone
          </label>
          <select id="club-tz" className="field-input" value={v.timeZone} onChange={set('timeZone')}>
            {TEAM_TIME_ZONES.map((z) => (
              <option key={z.id} value={z.id}>
                {z.label}
              </option>
            ))}
          </select>
          <p className="text-[13px] text-muted">New teams start in this time zone, and invites set for a date go out by it.</p>
        </div>
        {save.isError && !Object.keys(f).length && <Alert>{errorMessage(save.error)}</Alert>}
        <div className="flex items-center gap-3">
          <Button type="submit" loading={save.isPending}>
            Save changes
          </Button>
          {save.isSuccess && <span className="text-[14px] text-ok">Saved</span>}
        </div>
      </form>
    </Section>
  );
}

